/**
 * TvPill — mono UPPERCASE status / count pill on a -soft tint.
 *
 *   <TvPill variant="danger">OVERDUE</TvPill>
 *   <TvPill variant="success" icon={CheckCircle2}>DONE</TvPill>
 *   <TvPill variant="neutral">{openCount}</TvPill>
 *
 * Spec (per design_handoff_platform_redesign):
 *   JetBrains Mono · 10px · 600 · tracking 0.03em · h:20 · radius 999
 *   bg = variant.soft  ·  color = variant.ink
 *
 * Variants mirror v2 semantic tokens — see tvTokens for the palette.
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { tvTokens, type TvVariant } from './tokens';

interface TvPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: TvVariant;
  /** Optional leading Lucide icon (rendered at 11px). */
  icon?: LucideIcon;
  children: React.ReactNode;
}

const PALETTE: Record<TvVariant, { bg: string; color: string }> = {
  neutral: { bg: tvTokens.paper2, color: tvTokens.ink500 },
  accent: { bg: tvTokens.accentSoft, color: tvTokens.accentInk },
  success: { bg: tvTokens.successSoft, color: tvTokens.successInk },
  attention: { bg: tvTokens.attentionSoft, color: tvTokens.attentionInk },
  danger: { bg: tvTokens.dangerSoft, color: tvTokens.dangerInk },
};

export function TvPill({
  variant = 'neutral',
  icon: Icon,
  children,
  style,
  ...rest
}: TvPillProps) {
  const { bg, color } = PALETTE[variant];
  return (
    <span
      {...rest}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 20,
        padding: '0 9px',
        borderRadius: 999,
        fontFamily: tvTokens.fontMono,
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.03em',
        whiteSpace: 'nowrap',
        background: bg,
        color,
        ...style,
      }}
    >
      {Icon && <Icon size={11} />}
      {children}
    </span>
  );
}
